import AppContext from 'App/AppContext';
import { IPCheckbox, IPChoiceGroup, IPChoiceGroupProps, IPLabel, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import PeriodFilter, { IPeriodFilterData } from 'components/Filter/PeriodFilter';
import { getPeriodFilterEndDate, getPeriodFilterStartDate } from 'components/Filter/PeriodFilterHelper';
import KeyValueTagPicker from 'components/Pickers/KeyValueTagPicker';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import { globalStackTokensGapMedium, globalStackTokensGapSmall, globalTextStylesBold } from 'globalStyles';
import { TaskCompletedDeadlineStat } from 'models/dashboardScene';
import Tag from 'models/tag';
import { getTaskCompletedDeadlineMode, TaskCompletedDeadlineMode } from 'models/widget';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataTaskCompletedDeadline } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { fromApiDateOptional } from 'utils/datetime';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';

export class WidgetTaskCompletedDeadlineConfig {
  deadlineMode: TaskCompletedDeadlineMode;

  tagIds: number[];

  showTags: boolean;

  //resolved from tagIds when the config is loaded, not stored
  tags: Tag[];

  useAndForTagGroups: boolean;

  period?: IPeriodFilterData;

  constructor() {
    this.deadlineMode = TaskCompletedDeadlineMode.OnTime;
    this.tagIds = [];
    this.showTags = false;
    this.tags = [];
    this.useAndForTagGroups = false;
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.deadlineMode = getTaskCompletedDeadlineMode(raw);
        this.tagIds = newRawConfig.tagIds ?? [];
        this.showTags = newRawConfig.showTags ?? false;
        this.useAndForTagGroups = newRawConfig.useAndForTagGroups ?? false;
        this.period = newRawConfig.period;
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetTaskCompletedDeadlineConfig {
    const newConfig = new WidgetTaskCompletedDeadlineConfig();
    newConfig.deadlineMode = this.deadlineMode;
    newConfig.tagIds = [...this.tagIds];
    newConfig.tags = [...this.tags];
    newConfig.showTags = this.showTags;
    newConfig.useAndForTagGroups = this.useAndForTagGroups;
    newConfig.period = this.period;

    return newConfig;
  }
}

interface IWidgetTaskCompletedDeadlineProps extends IWidgetRendererProps {}

const WidgetTaskCompletedDeadline: FunctionComponent<IWidgetTaskCompletedDeadlineProps> = (
  props: IWidgetTaskCompletedDeadlineProps,
) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const projectId = props.projectId;
  const [data, setData] = useState<TaskCompletedDeadlineStat | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [config, setConfig] = useState<WidgetTaskCompletedDeadlineConfig | undefined>(undefined);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      await appContext.globalDataCache.tags.getItems();
      const config = loadConfig();
      setConfig(config);

      const customStart = fromApiDateOptional(config.period?.customStart);
      const customEnd = fromApiDateOptional(config.period?.customEnd);
      const start = getPeriodFilterStartDate(config.period?.option, config.period?.auditYear, customStart, appContext);
      const end = getPeriodFilterEndDate(config.period?.option, config.period?.auditYear, customEnd, appContext);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      const data = await apiGetWidgetDataTaskCompletedDeadline(
        config.tags,
        config.useAndForTagGroups,
        start,
        end,
        accessToken,
        props.abortSignal,
        projectId,
      );

      setData(data);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadConfig = (): WidgetTaskCompletedDeadlineConfig => {
    const newConfig = new WidgetTaskCompletedDeadlineConfig();
    newConfig.load(props.widget.widgetConfig);
    newConfig.tags = appContext.globalDataCache.tags.getItemsForId(newConfig.tagIds).filter((t) => t.tagId !== 0);

    if (props.period) {
      newConfig.period = props.period; //override period with the one from props
    }

    return newConfig;
  };

  //
  // Main render
  //
  if (!data || isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (data.completedCount === 0) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('widgets:TaskCompletedDeadline.NoTasks')}</IPText>
      </IPStack>
    );
  }

  const overDeadline = config?.deadlineMode === TaskCompletedDeadlineMode.OverDeadline;
  const count = overDeadline ? data.completedCount - data.onTimeCount : data.onTimeCount;
  //on time rounds down and over deadline rounds up: neither shows 100% or 0% while a task was late,
  //and both modes add up to 100%
  const ratio = (count / data.completedCount) * 100;
  const percentage = overDeadline ? Math.ceil(ratio) : Math.floor(ratio);

  return (
    <IPStack verticalFill horizontalAlign="center" tokens={globalStackTokensGapSmall}>
      {config && config.tags.length > 0 && config.showTags && (
        <IPStack.Item styles={{ root: { maxHeight: 44, overflowY: 'hidden' } }}>
          <KeyValueTags tags={config.tags} compact tagMaxWidth={200} />
        </IPStack.Item>
      )}
      <IPStack.Item grow>
        <IPStack verticalFill horizontalAlign="center" verticalAlign="center" tokens={globalStackTokensGapSmall}>
          <IPText variant="xxLargePlus" styles={globalTextStylesBold}>
            {`${count} / ${data.completedCount}`}
          </IPText>
          <IPText variant="xxLarge">{`${percentage}%`}</IPText>
          <IPText>
            {overDeadline
              ? t('widgets:TaskCompletedDeadline.CaptionOverDeadline')
              : t('widgets:TaskCompletedDeadline.CaptionOnTime')}
          </IPText>
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetTaskCompletedDeadline;

//
// Config
//

interface IWidgetConfigTaskCompletedDeadlineProps extends IWidgetConfigRendererProps {}

export const WidgetConfigTaskCompletedDeadline: FunctionComponent<IWidgetConfigTaskCompletedDeadlineProps> = (
  props: IWidgetConfigTaskCompletedDeadlineProps,
) => {
  const { t } = useTranslation(['widgets', 'translation', 'tasks']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [deadlineMode, setDeadlineMode] = useState<TaskCompletedDeadlineMode>(TaskCompletedDeadlineMode.OnTime);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [showTags, setShowTags] = useState<boolean>(false);
  const [useAndForTagGroups, setUseAndForTagGroups] = useState<boolean>(false);
  const [period, setPeriod] = useState<IPeriodFilterData | undefined>(undefined);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      await appContext.globalDataCache.tags.getItems();

      const config = new WidgetTaskCompletedDeadlineConfig();
      config.load(props.widget.widgetConfig);
      config.tags = appContext.globalDataCache.tags.getItemsForId(config.tagIds).filter((t) => t.tagId !== 0);

      if (props.period) {
        config.period = props.period; //override period with the one from props
      }

      setDeadlineMode(config.deadlineMode);
      setSelectedTags(config.tags);
      setShowTags(config.showTags);
      setUseAndForTagGroups(config.useAndForTagGroups);
      setPeriod(config.period);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateConfig = (
    deadlineMode: TaskCompletedDeadlineMode,
    tags: Tag[],
    showTags: boolean,
    useAndForTagGroups: boolean,
    period: IPeriodFilterData | undefined,
  ) => {
    const config = new WidgetTaskCompletedDeadlineConfig();
    config.deadlineMode = deadlineMode;
    config.tagIds = tags.map((t) => t.tagId);
    config.showTags = showTags;
    config.useAndForTagGroups = useAndForTagGroups;
    config.period = period;
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onSetDeadlineMode = (mode: TaskCompletedDeadlineMode) => {
    setDeadlineMode(mode);
    updateConfig(mode, selectedTags, showTags, useAndForTagGroups, period);
  };

  const deadlineModeOptions: IPChoiceGroupProps['options'] = [
    {
      key: TaskCompletedDeadlineMode.OnTime.toString(),
      text: t('widgets:TaskCompletedDeadline.Config.Mode.OnTime'),
    },
    {
      key: TaskCompletedDeadlineMode.OverDeadline.toString(),
      text: t('widgets:TaskCompletedDeadline.Config.Mode.OverDeadline'),
    },
  ];

  const onAdd = (tag: Tag) => {
    const newTags = [...selectedTags, tag];
    setSelectedTags(newTags);
    updateConfig(deadlineMode, newTags, showTags, useAndForTagGroups, period);
  };

  const onRemove = (tag: Tag) => {
    const newTags = selectedTags.filter((t) => t.tagId !== tag.tagId);
    setSelectedTags(newTags);
    updateConfig(deadlineMode, newTags, showTags, useAndForTagGroups, period);
  };

  const onSetShowTags = (checked: boolean) => {
    setShowTags(checked);
    updateConfig(deadlineMode, selectedTags, checked, useAndForTagGroups, period);
  };

  const onSetUseAndForTagGroups = (checked: boolean) => {
    setUseAndForTagGroups(checked);
    updateConfig(deadlineMode, selectedTags, showTags, checked, period);
  };

  const onSetPeriod = (period: IPeriodFilterData | undefined) => {
    setPeriod(period);
    updateConfig(deadlineMode, selectedTags, showTags, useAndForTagGroups, period);
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPChoiceGroup
          label={t('widgets:TaskCompletedDeadline.Config.Mode.Label')}
          selectedKey={deadlineMode.toString()}
          options={deadlineModeOptions}
          onChange={(ev, option) => {
            onSetDeadlineMode(
              option?.key === TaskCompletedDeadlineMode.OverDeadline.toString()
                ? TaskCompletedDeadlineMode.OverDeadline
                : TaskCompletedDeadlineMode.OnTime,
            );
          }}
        />
      </IPStack.Item>
      <IPStack.Item>
        <PeriodFilter
          showOption={true}
          showCurrentAuditYear={true}
          storageKey={undefined}
          onUpdatePeriodOption={onSetPeriod}
          period={period}
          showAll={true}
          disabled={props.widget.dashboard?.isContextType()}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPLabel>{t('widgets:TaskCompletedDeadline.Config.Label')}</IPLabel>
        <KeyValueTagPicker
          itemLimit={5}
          selectedTags={selectedTags}
          onAdd={onAdd}
          onRemove={onRemove}
          isLoading={isLoading}
          allowCreate={false}
        />
      </IPStack.Item>
      <IPCheckbox
        label={t('tasks:Filter.TagGroupAnd')}
        checked={useAndForTagGroups}
        onChange={(ev, checked) => {
          onSetUseAndForTagGroups(checked ?? false);
        }}
      />
      <IPStack.Item>
        <IPCheckbox
          checked={showTags}
          label={t('widgets:TaskCompletedDeadline.Config.LabelShowTags')}
          onChange={(ev, checked) => {
            onSetShowTags(checked ?? false);
          }}
        />
      </IPStack.Item>
    </IPStack>
  );
};
